import React, { useState } from 'react';
import {
  aiSettingsApi,
  AiSettings,
  CatalogueModel,
  ModelInput,
  PROVIDER_KEYS,
  ProviderKey,
} from '../../api/ai-settings';
import { useToast } from '../../hooks/useToast';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { TierBadge } from './TierBadge';
import { usdPerM } from './format';

interface Props {
  settings: AiSettings;
  catalogue: CatalogueModel[];
  canManage: boolean;
  onChanged: () => void;
}

const muted: React.CSSProperties = { fontSize: 12, color: 'var(--neutral-600)' };
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 12, color: 'var(--neutral-600)' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: 14, borderTop: '1px solid var(--neutral-200)' };

const EMPTY = {
  provider: 'anthropic' as ProviderKey,
  modelId: '',
  name: '',
  input: '',
  output: '',
  vision: false,
  tools: true,
  effort: '',
};

/**
 * Every model PayIQ knows, by vendor, with its price and cost label. The
 * label is worked out from the price and the two thresholds below, never
 * typed in: change a price and the label follows.
 */
export const ModelsTab: React.FC<Props> = ({ settings, catalogue, canManage, onChanged }) => {
  const toast = useToast();
  const [low, setLow] = useState(String(settings.tiers.lowBelowUsd));
  const [high, setHigh] = useState(String(settings.tiers.highFromUsd));
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const tiersDirty =
    Number(low) !== settings.tiers.lowBelowUsd || Number(high) !== settings.tiers.highFromUsd;

  const saveTiers = async () => {
    setBusy(true);
    try {
      await aiSettingsApi.setTiers({ lowBelowUsd: Number(low), highFromUsd: Number(high) });
      toast.success('Cost labels updated.');
      onChanged();
    } catch (err) {
      toast.error(err, 'Could not save the thresholds.');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (m: CatalogueModel) => {
    try {
      await aiSettingsApi.updateModel(m.id, { active: !m.active });
      onChanged();
    } catch (err) {
      toast.error(err, 'Could not change the model.');
    }
  };

  const create = async () => {
    const body: ModelInput = {
      provider: form.provider,
      modelId: form.modelId.trim(),
      name: form.name.trim(),
      inputUsdPerMTok: Number(form.input),
      outputUsdPerMTok: Number(form.output),
      supportsVision: form.vision,
      supportsTools: form.tools,
      ...(form.effort.trim() ? { reasoningEffort: form.effort.trim() } : {}),
    };
    setBusy(true);
    try {
      await aiSettingsApi.createModel(body);
      toast.success('Model added.');
      setAdding(false);
      setForm(EMPTY);
      onChanged();
    } catch (err) {
      toast.error(err, 'Could not add the model.');
    } finally {
      setBusy(false);
    }
  };

  const valid =
    form.modelId.trim() && form.name.trim() && form.input !== '' && form.output !== '' &&
    Number(form.input) >= 0 && Number(form.output) >= 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Card>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Cost labels</h2>
        <p style={{ ...muted, marginBottom: 12 }}>
          Based on the blended price: (3 × input + output) ÷ 4 per million tokens. Below the first
          value is Low, from the second is High, between is Medium.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Input label="Low below ($/M)" type="number" min={0} step="0.01" value={low} disabled={!canManage} onChange={(e) => setLow(e.target.value)} />
          <Input label="High from ($/M)" type="number" min={0} step="0.01" value={high} disabled={!canManage} onChange={(e) => setHigh(e.target.value)} />
          {canManage && (
            <Button onClick={saveTiers} disabled={!tiersDirty} loading={busy}>
              Save labels
            </Button>
          )}
        </div>
      </Card>

      {canManage && (
        <div>
          <Button variant="secondary" onClick={() => setAdding(true)}>
            Add model
          </Button>
        </div>
      )}

      {PROVIDER_KEYS.map((key) => {
        const models = catalogue.filter((m) => m.provider === key);
        if (models.length === 0) return null;
        const label = settings.providers.find((p) => p.key === key)?.label ?? key;
        return (
          <Card key={key} padding={0}>
            <h2 style={{ fontSize: 16, fontWeight: 600, padding: '16px 16px 8px' }}>{label}</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Model</th>
                    <th style={th}>Input / output per M</th>
                    <th style={th}>Cost</th>
                    <th style={th}>Can do</th>
                    <th style={th}>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {models.map((m) => (
                    <tr key={m.id}>
                      <td style={td}>
                        <div>{m.name}</div>
                        <div style={muted}>
                          {m.modelId}
                          {m.reasoningEffort ? ` · effort ${m.reasoningEffort}` : ''}
                        </div>
                        {m.priceNote && <div style={muted}>{m.priceNote}</div>}
                      </td>
                      <td style={td}>
                        {usdPerM(m.inputUsdPerMTok)} / {usdPerM(m.outputUsdPerMTok)}
                      </td>
                      <td style={td}>
                        <TierBadge tier={m.tier} />
                      </td>
                      <td style={td}>
                        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {m.supportsTools && <Badge variant="neutral">Tools</Badge>}
                          {m.supportsVision && <Badge variant="neutral">Images</Badge>}
                        </span>
                      </td>
                      <td style={td}>
                        <input
                          type="checkbox"
                          aria-label={`${m.name} active`}
                          checked={m.active}
                          disabled={!canManage}
                          onChange={() => toggle(m)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}

      <Modal isOpen={adding} onClose={() => setAdding(false)} title="Add a model">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 500 }}>
            Provider
            <select
              value={form.provider}
              onChange={(e) => setForm({ ...form, provider: e.target.value as ProviderKey })}
              style={{ display: 'block', marginTop: 6, padding: '8px 10px', width: '100%' }}
            >
              {PROVIDER_KEYS.map((k) => (
                <option key={k} value={k}>
                  {settings.providers.find((p) => p.key === k)?.label ?? k}
                </option>
              ))}
            </select>
          </label>
          <Input label="Model ID" value={form.modelId} onChange={(e) => setForm({ ...form, modelId: e.target.value })} />
          <Input label="Display name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Input $ per M tokens" type="number" min={0} step="0.001" value={form.input} onChange={(e) => setForm({ ...form, input: e.target.value })} />
          <Input label="Output $ per M tokens" type="number" min={0} step="0.001" value={form.output} onChange={(e) => setForm({ ...form, output: e.target.value })} />
          <Input label="Reasoning effort (optional)" value={form.effort} onChange={(e) => setForm({ ...form, effort: e.target.value })} />
          <label style={{ fontSize: 13 }}>
            <input type="checkbox" checked={form.tools} onChange={(e) => setForm({ ...form, tools: e.target.checked })} /> Supports tool calling
          </label>
          <label style={{ fontSize: 13 }}>
            <input type="checkbox" checked={form.vision} onChange={(e) => setForm({ ...form, vision: e.target.checked })} /> Supports image input
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button onClick={create} disabled={!valid} loading={busy}>
              Add model
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
